import CardSheet from 'app/pages/cards/cards/CardSheet/CardSheet';
import NewCardSheet from 'app/pages/cards/cards/NewCardSheet/NewCardSheet';
import ActivateCardSheet from 'app/pages/cards/cards/ActivateCardSheet';
import SidesheetContainer from 'app/shared-components/sidesheets/SidesheetContainer';
import AssignCardSidesheet from 'app/pages/cards/cards/AssignCardSheet/AssignCardSidesheet';
import TransactionAdvancedSearchSidesheet from 'app/pages/overview/tabs/transactions/TransactionAdvancedSearchSidesheet/TransactionAdvancedSearchSidesheet';
import MobileCardSidesheet from 'app/pages/mobile/pages/mobile-home/components/MobileCardSidesheet';
import { ReactNode, createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum SidesheetRoutes {
  AssignCard = 'AssignCard',
  Card = 'Card',
  ActivateCard = 'ActivateCard',
  NewCard = 'NewCard',
  TransactionsAdvancedSearch = 'TransactionAdvancedSeach',
  MobileCard = 'MobileCard',
}

const SidesheetContext = createContext({} as any);

export function SidesheetProvider({ children }: { children: ReactNode }) {
  const [sidesheets, setSidesheets] = useState<{
    [key in SidesheetRoutes]: {
      isOpen: boolean;
      params: unknown;
      width?: string;
      customHeader?: boolean;
      sx?: any;
    };
  }>({
    [SidesheetRoutes.AssignCard]: {
      isOpen: false,
      params: {},
      width: '100%',
      sx: { minWidth: { sm: '100%', md: '600px' } },
    },
    [SidesheetRoutes.Card]: {
      isOpen: false,
      params: {},
      width: '100%',
      customHeader: true,
      sx: { minWidth: { sm: '100%', md: '900px', lg: '1200px' } },
    },
    [SidesheetRoutes.ActivateCard]: {
      isOpen: false,
      params: {},
      width: '100%',
      customHeader: true,
    },
    [SidesheetRoutes.NewCard]: {
      isOpen: false,
      params: {},
      width: '900px',
      customHeader: true,
      sx: { minWidth: { sm: '100%', md: '600px' } },
    },
    [SidesheetRoutes.TransactionsAdvancedSearch]: {
      isOpen: false,
      params: {},
      width: '600px',
      sx: { minWidth: { sm: '100%', md: '600px' }, maxWidth: { sm: '100vw', md: '600px' } },
    },
    [SidesheetRoutes.MobileCard]: {
      isOpen: false,
      params: {},
      width: '100%',
      sx: { minWidth: { xs: '100%', sm: '100%', md: '500px' } },
    },
  });
  const { t } = useTranslation();
  const context = useMemo(() => {
    return {
      sidesheets,
      openSidesheet(sidesheetId: SidesheetRoutes, params: unknown) {
        if (sidesheets[sidesheetId].isOpen) return;

        setSidesheets((prevSidesheets) => ({
          ...prevSidesheets,
          [sidesheetId]: {
            ...prevSidesheets?.[sidesheetId],
            isOpen: true,
            params,
          },
        }));
      },
      closeSidesheet(sidesheetId: SidesheetRoutes) {
        if (!sidesheets[sidesheetId].isOpen) return;

        setSidesheets((prevSidesheets) => ({
          ...prevSidesheets,
          [sidesheetId]: {
            ...prevSidesheets?.[sidesheetId],
            isOpen: false,
            params: {},
          },
        }));
      },
    };
  }, [sidesheets]);

  return (
    <SidesheetContext.Provider value={context}>
      {children}

      <SidesheetContainer title={t('assignCardToCardholder')} sidesheetId={SidesheetRoutes.AssignCard}>
        <AssignCardSidesheet />
      </SidesheetContainer>

      <SidesheetContainer sidesheetId={SidesheetRoutes.Card}>
        <CardSheet />
      </SidesheetContainer>

      <SidesheetContainer sidesheetId={SidesheetRoutes.ActivateCard}>
        <ActivateCardSheet />
      </SidesheetContainer>

      <SidesheetContainer sidesheetId={SidesheetRoutes.NewCard}>
        <NewCardSheet />
      </SidesheetContainer>

      <SidesheetContainer
        title={t('advancedFiltering')}
        sidesheetId={SidesheetRoutes.TransactionsAdvancedSearch}
      >
        <TransactionAdvancedSearchSidesheet />
      </SidesheetContainer>

      <SidesheetContainer sidesheetId={SidesheetRoutes.MobileCard}>
        <MobileCardSidesheet />
      </SidesheetContainer>
    </SidesheetContext.Provider>
  );
}

export function useSidesheet() {
  return useContext(SidesheetContext);
}
